import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthState, LoginCredentials, SignupCredentials } from '../types/auth';
import { supabase } from '../utils/supabase';

// UUID形式のチェック関数
const isValidUUID = (uuid: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,

      initializeAuth: async () => {
        try {
          // Try to restore session from storage
          const sessionKey = 'sb-session';
          let sessionValue = localStorage.getItem(sessionKey) || sessionStorage.getItem(sessionKey);
          if (sessionValue) {
            try {
              const session = JSON.parse(sessionValue);
              if (session && session.access_token) {
                await supabase.auth.setSession(session);
              }
            } catch (e) {
              // Ignore parse errors
            }
          }

          const { data, error } = await supabase.auth.getSession();
          if (error) {
            console.error("Session error during initialization:", error);
            await get().logout();
            return;
          }

          if (!data.session || !data.session.user) {
            console.log("No active session found");
            set({ user: null, isAuthenticated: false });
            return;
          }

          const supabaseUser = data.session.user;
          
          if (!isValidUUID(supabaseUser.id)) {
            console.error('Invalid user ID format:', supabaseUser.id);
            await get().logout();
            return;
          }

          // Verify user exists in auth.users table by trying to get current user
          const { data: userData, error: userError } = await supabase.auth.getUser();
          if (userError || !userData.user) {
            console.error('User verification failed:', userError);
            await get().logout();
            return;
          }

          const {data: currentUserData, error: currentUserError} = await supabase.from('users').select('*').eq('id', supabaseUser.id).single();
          if (currentUserError) {
            console.error('Failed to fetch user from users table:', currentUserError);
            await get().logout();
            return;
          }
          if (!currentUserData) {
            console.error('User not found in users table');
            await get().logout();
            return;
          }

          const user = {
            id: supabaseUser.id,
            email: supabaseUser.email || '',
            role: currentUserData.role || 'user',
            plan: currentUserData.plan || 'unsubscribed',
            createdAt: supabaseUser.user_metadata?.createdAt || supabaseUser.created_at,
            updatedAt: supabaseUser.user_metadata?.updatedAt || supabaseUser.updated_at,
            lastLoginAt: supabaseUser.user_metadata?.lastLoginAt || new Date().toISOString(),
            isVerified: supabaseUser.email_confirmed_at !== null,
            settings: supabaseUser.user_metadata?.settings || {},
          };

          console.log('Authentication initialized for user:', user.email);
          set({ user, isAuthenticated: true });
        } catch (error) {
          console.error('Error during auth initialization:', error);
          set({ user: null, isAuthenticated: false });
          localStorage.clear();
        }
      },

      signup: async ({ email, password, referralCode }: SignupCredentials) => {
        const trimmedEmail = email.trim().toLowerCase();
        try {
          const { data, error } = await supabase.auth.signUp({
            email: trimmedEmail,
            password,
            options: {
              data: {
                role: 'user',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                lastLoginAt: new Date().toISOString(),
              },
            },
          });

          if (error) throw new Error(error.message);

            // 登録直後に users テーブルへレコードを作成
            if (data.user?.id) {
              await supabase
                .from('users')
                .insert([{
                  id: data.user.id,
                  email: email,
                  role: 'user',
                  referral_code: referralCode ? referralCode.trim() : null,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                }]);

              // Create initial user_credits record after signup
              await supabase
                .from('user_credits')
                .insert([{
                  user_id: data.user.id,
                  plan_credits: 10,
                  max_plan_credits: 10,
                  monthly_usage: 0,
                  max_products: 30,
                  last_reset_date: new Date().toISOString(),
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                }]);
            }


        } catch (error) {
          console.error('Signup error:', error);
          throw error;
        }
      },

      login: async ({ email, password, rememberMe = false, recaptchaToken }: LoginCredentials) => {
        // Brute force protection: track failed attempts in localStorage
        const maxAttempts = 10;
        const lockoutMinutes = 15;
        const attemptsKey = `login-attempts-${email}`;
        const lockoutKey = `login-lockout-${email}`;
        const now = Date.now();
        const lockoutUntil = localStorage.getItem(lockoutKey);
        if (lockoutUntil && now < parseInt(lockoutUntil, 10)) {
          throw new Error(`一定回数以上ログインに失敗したため、${lockoutMinutes}分間ログインできません。`);
        }

        // Input validation (basic XSS/SQLi prevention)
        const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
        if (!emailRegex.test(email)) {
          throw new Error('メールアドレスの形式が正しくありません');
        }
        if (password.length < 8 || /[<>"'\\]/.test(password)) {
          throw new Error('パスワードの形式が正しくありません');
        }

        // Require reCAPTCHA after 3 failed attempts
        const attempts = parseInt(localStorage.getItem(attemptsKey) || '0', 10);
        if (attempts >= 5) {
          if (!recaptchaToken) {
            throw new Error('reCAPTCHA認証が必要です');
          }
          // TODO: Verify reCAPTCHA token on your backend for production security
        }

        try {
          const { data, error } = await supabase.auth.signInWithPassword({ email, password });

          if (error) {
            // Increment failed attempts
            let attempts = parseInt(localStorage.getItem(attemptsKey) || '0', 10) + 1;
            localStorage.setItem(attemptsKey, attempts.toString());
            if (attempts >= maxAttempts) {
              localStorage.setItem(lockoutKey, (now + lockoutMinutes * 60 * 1000).toString());
              localStorage.removeItem(attemptsKey);
              throw new Error(`一定回数以上ログインに失敗したため、${lockoutMinutes}分間ログインできません。`);
            }
            throw error;
          }

          // On success, clear failed attempts
          localStorage.removeItem(attemptsKey);
          localStorage.removeItem(lockoutKey);

          if (data.session) {
            // Store session in appropriate storage
            const sessionKey = 'sb-session';
            const sessionValue = JSON.stringify(data.session);
            if (rememberMe) {
              localStorage.setItem(sessionKey, sessionValue);
              sessionStorage.removeItem(sessionKey);
            } else {
              sessionStorage.setItem(sessionKey, sessionValue);
              localStorage.removeItem(sessionKey);
            }
          }

          if (data.user) {
            if (!isValidUUID(data.user.id)) {
              console.error('Invalid user ID format:', data.user.id);
              throw new Error('無効なユーザーセッションが返されました。再度ログインしてください。');
            }

            await supabase.auth.updateUser({
              data: { lastLoginAt: new Date().toISOString() },
            });

            const { data: userData } = await supabase.auth.getUser();

            if (userData.user && isValidUUID(userData.user.id)) {
              const {data: currentUserData, error: currentUserError} = await supabase.from('users').select('*').eq('id', userData.user.id).single();
              if (currentUserError) {
                console.error('Failed to fetch user from users table:', currentUserError);
                await get().logout();
                return;
              }
              if (!currentUserData) {
                console.error('User not found in users table');
                await get().logout();
                return;
              }

              const user = {
                id: userData.user.id,
                email: userData.user.email || '',
                role: currentUserData.role || 'user',
                plan: currentUserData.plan || 'unsubscribed',
                createdAt: userData.user.user_metadata?.createdAt || userData.user.created_at,
                updatedAt: userData.user.user_metadata?.updatedAt || userData.user.updated_at,
                lastLoginAt: new Date().toISOString(),
                isVerified: userData.user.email_confirmed_at !== null,
                settings: userData.user.user_metadata?.settings || {},
              };

              set({
                user,
                isAuthenticated: true,
              });
            } else {
              throw new Error('Invalid user data received');
            }
          }
        } catch (error) {
          console.error('Login error:', error);
          set({ user: null, isAuthenticated: false });
          throw error;
        }
      },

      logout: async () => {
        try {
          // 1. 現在のセッションを取得
          const { data, error: sessionErr } = await supabase.auth.getSession();
          // getSession() が 401 を返す場合は無視
          if (sessionErr && sessionErr.status !== 401) {
            console.error('getSession error:', sessionErr);
          }

          // 2. セッションが存在するときだけ signOut()
          if (data?.session) {
            const { error: signOutErr } = await supabase.auth.signOut();
            // "session_not_found" 系のエラーは無視
            if (signOutErr && !signOutErr.message.includes('session')) {
              console.error('signOut error:', signOutErr);
            }
          }
        } catch (err) {
          // 予期しない例外のみログ出力
          if (
            !(err instanceof Error) ||
            (!err.message.includes('session') && !err.message.includes('Auth session missing'))
          ) {
            console.error('Logout exception:', err);
          }
        } finally {
          // 3. ストアと localStorage は必ずクリア
          set({ user: null, isAuthenticated: false });
          localStorage.clear();
        }
      },

      updateUserSettings: async (settings) => {
        const { user } = get();
        if (!user) return;

        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError || !userData.user) {
          console.error('User verification failed:', userError);
          
          // If user doesn't exist in auth system, clear invalid session
          if (userError?.message?.includes('User from sub claim in JWT does not exist') || 
              userError?.code === 'user_not_found') {
            console.log('Invalid JWT detected, clearing session...');
            await get().logout();
          } else {
            set({ user: null, isAuthenticated: false });
            localStorage.clear();
          }
          return;
        }

        try {
          const { error } = await supabase.auth.updateUser({
            data: {
              settings: {
                ...user.settings,
                ...settings,
              },
              updatedAt: new Date().toISOString(),
            },
          });

          if (error) throw error;

          set({
            user: {
              ...user,
              settings: {
                ...user.settings,
                ...settings,
              },
              updatedAt: new Date().toISOString(),
            },
          });
        } catch (error) {
          console.error('Error updating user settings:', error);
          throw new Error('設定の更新に失敗しました');
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);